import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import OrdersSelectBar from './OrdersSelectBar';
import { LINKS } from '../../../../../Routs/config';
import { Surface, ScrollArea } from '../../../../../generic/ui/Surface';
import { Loader } from '../../../../../generic/ui/Loader';
import { createGroupColorMap } from '../../../../../generic/ui/colors';
import OrderRowItem from './OrderRowItem';
import { CreateButton, CreateFab } from '../../../../../generic/ui/CreateButton';
import usePrint from '../../../../../generic/hooks/usePrint';
import { PdfPreview } from '../../../../../generic/ui/PdfPreview';
import { AppModal } from '../../../../../generic/ui/Modal';
import { OrderItem, OrderUser, ProductGroup, SortableField } from '../types';
import { OrderStatusValue } from '../../../../../generic/ui/Selectors/OrderStatusSelector';
import { OrderAkpacaTypeValue } from '../../../../../generic/ui/Selectors/OrderAkpacaTypeSelector';
import { IWarhouseListItem } from '../../../../../modules/API/Defaults/models/warhouseList';

const ORDER_PDF_URL = 'v2/order';

const CREATE_LABEL = 'הזמנה חדשה';
const CREATE_BLOCKED_LABEL = 'כרגע לא ניתן לבצע הזמנה';

interface Props {
    azmanot: OrderItem[];
    input: string;
    user: OrderUser;
    isOpenLogs: boolean;
    isBlockCreateOrder: boolean;
    isLoading?: boolean;
    statusId: OrderStatusValue;
    warehouseID: number;
    typeFilter: OrderAkpacaTypeValue;
    warehouseList: IWarhouseListItem[] | null;
    groupID: number;
    groups: ProductGroup[];
    onOpenLogs: () => void;
    onFilterByInput: (value: string) => void;
    onStatusChange: (value: OrderStatusValue) => void;
    onWarehouseChange: (id: number) => void;
    onTypeChange: (value: OrderAkpacaTypeValue) => void;
    onGroupChange: (groupID: number) => void;
    onSortClick: (field: SortableField) => void;
    onRemoveOrder: (orderID: number) => Promise<void>;
    onInnerTimeChange: (value: string) => void;
    setSelectedDateStart: (value: string | null) => void;
    setSelectedDateEnd: (value: string | null) => void;
}

const OrderListView: React.FC<Props> = ({
    azmanot,
    input,
    isBlockCreateOrder,
    isLoading = false,
    statusId,
    warehouseID,
    typeFilter,
    warehouseList,
    groupID,
    groups,
    onFilterByInput,
    onStatusChange,
    onWarehouseChange,
    onTypeChange,
    onGroupChange,
    onRemoveOrder,
    onInnerTimeChange,
    setSelectedDateStart,
    setSelectedDateEnd,
}) => {
    const isEmpty = !azmanot || azmanot.length < 1;
    const lastIndex = (azmanot?.length ?? 0) - 1;
    const getGroupColor = useMemo(
        () => createGroupColorMap(azmanot?.map((o) => o.GroupDesc) ?? []),
        [azmanot],
    );

    const {
        file,
        handleSharePdf,
        handleDownloadPdf,
        handleClearFile,
        handleOpenPdf,
        handleGetPdf,
    } = usePrint();

    const handlePrintOrder = useCallback(
        (orderID: number) => {
            handleGetPdf(`${ORDER_PDF_URL}/${orderID}/pdf`);
        },
        [handleGetPdf],
    );

    return (
        <>
            {file && (
                <AppModal
                    size='lg'
                    title='תצוגה מקדימה'
                    controlFunc={handleClearFile}
                    Children={PdfPreview}
                    childrenProps={{
                        file,
                        handleSharePdf,
                        handleDownloadPdf,
                        handleClearFile,
                        handleOpenPdf,
                    }}
                />
            )}
            <BarRow>
                <CreateButton
                    to={LINKS.orders.clientOrders.create}
                    label={CREATE_LABEL}
                    disabledLabel={CREATE_BLOCKED_LABEL}
                    disabled={isBlockCreateOrder}
                />
                <OrdersSelectBar
                    input={input}
                    setInput={onFilterByInput}
                    setSelectedDateStart={setSelectedDateStart}
                    setSelectedDateEnd={setSelectedDateEnd}
                    statusId={statusId}
                    onStatusChange={onStatusChange}
                    warehouseID={warehouseID}
                    onWarehouseChange={onWarehouseChange}
                    warehouseList={warehouseList}
                    typeFilter={typeFilter}
                    onTypeChange={onTypeChange}
                    groupID={groupID}
                    onGroupChange={onGroupChange}
                    groups={groups}
                />

            </BarRow>
            <Surface>
                <ScrollArea>
                    
                    {isLoading ? (
                        <CenterState>
                            <Loader size={56} />
                        </CenterState>
                    ) : isEmpty ? (
                        <EmptyState>
                            <img src='/images/no-data.png' alt='empty data' />
                        </EmptyState>
                    ) : (
                        azmanot.map((order, idx) => (
                            <OrderRowItem
                                key={order.OrderID}
                                {...order}
                                removeAzmanot={onRemoveOrder}
                                setInnerTime={onInnerTimeChange}
                                onPrint={handlePrintOrder}
                                isLast={idx === lastIndex}
                                tagColor={getGroupColor(order.GroupDesc)}
                            />
                        ))
                    )}
                </ScrollArea>
            </Surface>
            <CreateFab
                to={LINKS.orders.clientOrders.create}
                label={CREATE_LABEL}
                disabledLabel={CREATE_BLOCKED_LABEL}
                disabled={isBlockCreateOrder}
            />
        </>
    );
};

export default OrderListView;

const BarRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
`;

const EmptyState = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
`;

const CenterState = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
`;
